import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, LogOut } from 'lucide-react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useAgents } from '../hooks/useAgents';
import { useBillingUsage, useCreditsBalance } from '../hooks/useBilling';

export default function SidebarLayout({ children }) {
  const location = useLocation();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { data } = useAgents();
  const agents = data?.agents || [];

  const { data: usageData } = useBillingUsage({ enabled: !!user });
  const { data: creditsData } = useCreditsBalance({ enabled: !!user });
  const currentTier = usageData?.tier || creditsData?.tier || "free";

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem('tenantSlug');
    signOut();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const isActive = (path) => {
    if (path === '/agents') {
      return location.pathname === '/agents' || location.pathname.startsWith('/agents/');
    }
return location.pathname === path;
  };

  const email = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses[0]?.emailAddress;
  const initials = user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'U' : 'U';
  const displayName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User' : 'User';

  const navLinkStyle = (path) => {
    const active = isActive(path);
    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 20px',
      fontSize: '13px',
      color: active ? '#f2f2f2' : '#888',
      borderLeft: `2px solid ${active ? '#ffffff' : 'transparent'}`,
      background: active ? 'rgba(255,255,255,0.02)' : 'transparent',
      transition: 'color 0.1s',
      textDecoration: 'none',
      fontFamily: 'Inter, sans-serif',
    };
  };

  const sections = [
    {
      label: 'CORE',
      items: [
        { name: 'Agents', path: '/agents', badge: agents.length || null },
        { name: 'Signals', path: '/signals' },
        { name: 'Analytics', path: '/analytics' },
      ],
    },
    {
      label: 'ACCOUNT',
      items: [
        { name: 'Billing', path: '/billing' },
        { name: 'Settings', path: '/settings' },
      ],
    },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Fixed Sidebar */}
      <aside
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100vh',
          width: 200,
          background: '#0d0d0d',
          borderRight: '1px solid rgba(255,255,255,0.07)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Logo */}
        <div
          style={{
            height: 52,
            display: 'flex',
            alignItems: 'center',
            padding: '0 20px',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}
        >
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#f2f2f2',
            }}
          >
            KALIBR
          </span>
        </div>

        {/* Nav sections */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {sections.map((section) => (
            <div key={section.label}>
              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 9,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: '#252525',
                  padding: '18px 20px 6px',
                }}
              >
                {section.label}
              </div>
              <nav>
                {section.items.map((item) => (
                  <NavLink
                    key={`${section.label}-${item.name}`}
                    to={item.path}
                    style={navLinkStyle(item.path)}
                    onMouseEnter={(e) => {
                      if (!isActive(item.path)) {
                        e.currentTarget.style.color = '#c0c0c0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive(item.path)) {
                        e.currentTarget.style.color = '#888';
                      }
                    }}
                  >
                    <span>{item.name}</span>
                    {item.badge != null && item.badge > 0 && (
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: '10.5px',
                          color: '#555',
                        }}
                      >
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                ))}
              </nav>
            </div>
          ))}
        </div>

        {/* Footer */}
        {user && (
          <div
            style={{
              position: 'relative',
              borderTop: '1px solid rgba(255,255,255,0.07)',
              padding: '10px 16px',
            }}
            ref={dropdownRef}
          >
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                width: '100%',
                textAlign: 'left',
              }}
            >
              {/* Avatar */}
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: '#1a1a1a',
                  border: '1px solid rgba(255,255,255,0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#f2f2f2',
                  fontFamily: 'Inter, sans-serif',
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#f2f2f2',
                    fontFamily: 'Inter, sans-serif',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {displayName}
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10,
                    color: '#555',
                  }}
                >
                  {currentTier}
                </div>
              </div>
              <ChevronDown size={14} color="#555" style={{ flexShrink: 0 }} />
            </button>

            {showDropdown && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  width: 200,
                  marginBottom: 4,
                  background: '#1a1a1a',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  zIndex: 50,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <div style={{ fontSize: 11, color: '#555', marginBottom: 2 }}>Signed in as</div>
                  <div
                    style={{
                      fontSize: 12,
                      color: '#ccc',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {email}
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '10px 14px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 13,
                    color: '#ef4444',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  <LogOut size={14} />
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </aside>

      {/* Main content */}
      <main style={{ marginLeft: 200, flex: 1 }}>
        {children}
      </main>
    </div>
  );
}
